// deps
import React, { ReactElement, memo, useContext, useMemo, useCallback, useRef } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { useEffectOnce } from "@swiggy-private/core-ui";

// components
import { CART_WIDGET_TYPES, VALID_RENDERING_DETAILS_TYPE } from "@swiggy-private/cart-ui";
import MenuItemWidget from "@cart-client/components/cart-menu-items-and-suggestions/component/MenuItemWidget";
import { CrossSellM2ItemRenderer } from "@cart-client/components/cross-sell-m2-item-renderer";
import { FreebieWidgetRenderer } from "@cart-client/components/freebie-widget-renderer";
import { StealDealWidgetRenderer } from "@cart-client/components/steal-deal-widget-renderer";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// hooks
import { useCrossSellAnalytics } from "@cart-client/hooks/useCrossSellAnalytics";
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";
import { useMenuCalloutAnalytics } from "@cart-client/hooks/useMenuCalloutAnalytics";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";
import { useMROCartItemSectionAnimation } from "@cart-client/hooks/useMROCartItemSectionAnimation";

// helpers
import { getIrctcIdentifier } from "@cart-client/helpers/Cart";
import { isBulkOrder } from "@cart-client/helpers/BulkOrder";
import { AnalyticsContext, isPopFlow, useSessionStorage } from "@swiggy-private/checkout-core";
import { filterRenderingDetails } from "@cart-client/helpers/CartRendering";
import { isCartContainBoltItems } from "@cart-client/helpers/Cart";
import {
    getMROChildCartByRestaurantId,
    getMROChildCartRestaurantId,
    isMROFlow,
    logMROEventsIntoNR,
} from "@cart-client/helpers/MROFoodCart";

// analytics
import { impressionCartWidgetRanking } from "@cart-client/analytics/events";

//constans
import { DEFAULT_WIDGET_SECTIONS } from "./constants";
import { IS_IRCTC_CART_SESSION_KEY } from "@cart-client/constants/StorageKeys";

//types and inerfaces
import {
    ItemImagesOnCartXPVariants,
    FreebieWidgetRenderingDetails,
    StealDealWidgetRenderingDetails,
    RenderingDetails,
} from "@swiggy-private/cart-types";
import { CartMenuItemsAndSuggestionsProps } from "@cart-client/interfaces/CartMenuItemsAndSuggestions";
import { getFreebieWidgetData, shouldShowFreebieWidget } from "@cart-client/helpers/FreebieWidget";
import { getStealDealWidgetData, shouldShowStealDealWidget } from "@cart-client/helpers/StealDealWidget";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

function CartMenuItemsAndSuggestions({
    isValueMeal,
    isCroutonVisible,
    renderingDetails,
    goToMenu,
}: CartMenuItemsAndSuggestionsProps): ReactElement | null {
    const { cartState } = useContext(CartContext);
    const { data } = useContext(CartFlowContext);
    const isMROCart = isMROFlow(data?.cartSource);

    const { reviewedCart: cartResponse } = cartState;

    const { impressionEvent } = useContext(AnalyticsContext);

    const isCartBoltEnabled = isCartContainBoltItems(cartResponse?.data);

    const { onImpressionCrossSellM2Item } = useCrossSellAnalytics(cartResponse);

    const {
        onImpressionAddItemCarousel,
        onClickAddItemCarousel,
        onClickItemDetailsCarousel,
        onImpressionCarouselItemDetailsPopup,
        onImpressionCartItemDetails,
        onImpressionMagicItemsUnlockPopup,
        onClickDismissMagicItemsUnlockPopup,
        onClickExploreMenuMagicItems,
    } = useCartRendererAnalytics(cartResponse);

    const menuItemsCalloutAnalytics = useMenuCalloutAnalytics(cartResponse);

    const { itemImagesXP } = useCartRendererXP(cartResponse);

    const isBulkOrderFn = useMemo(() => {
        return isBulkOrder(cartResponse);
    }, [cartResponse]);

    const itemImageOnCartXpFlagExcludingBulkOrder = useMemo(() => {
        return isBulkOrderFn ? ItemImagesOnCartXPVariants.DEFAULT : itemImagesXP;
    }, [isBulkOrderFn, itemImagesXP]);

    const isPopCart = isPopFlow();

    const disableRedirectionToMenu = isValueMeal;

    const [isIrctcCartSession] = useSessionStorage(IS_IRCTC_CART_SESSION_KEY, !!getIrctcIdentifier(cartResponse));

    const widgetSections = useMemo(() => {
        const widgetSections = filterRenderingDetails(renderingDetails, CART_WIDGET_TYPES);
        return !isEmpty(widgetSections) ? widgetSections : DEFAULT_WIDGET_SECTIONS;
    }, [renderingDetails]);

    const isMRO = isMROFlow();
    const wrapperRef = useRef<HTMLDivElement>(null);

    // MRO-only: grow-in / collapse-out animation for menu sections on tab toggle.
    useMROCartItemSectionAnimation(wrapperRef, isMRO);

    const menuItemsWidget = useCallback(
        (isMROCart: boolean, restaurantId?: number | string) => {
            if (isEmpty(cartResponse)) {
                return null;
            }

            let key;
            if (isMROCart && !isEmpty(restaurantId)) {
                const mroCart = getMROChildCartByRestaurantId(cartResponse as MROReviewedCart, restaurantId);
                if (isEmpty(mroCart)) {
                    logMROEventsIntoNR({
                        type: "mro_cart_rendering_no_child_cart_found",
                        restaurantId: String(restaurantId),
                    });
                }
                key = getMROChildCartRestaurantId(mroCart);
            } else {
                // BAU cart: the restaurant cannot change once the cart is open for a Rx, so the
                // restaurant id is a stable key and avoids an undefined key without forcing remounts.
                key = String(cartResponse?.data?.restaurant_details?.id || "");
            }
            return (
                <MenuItemWidget
                    key={key}
                    isMROCart={isMROCart}
                    restaurantId={restaurantId}
                    menuItemsCalloutAnalytics={menuItemsCalloutAnalytics}
                    isBulkOrderFn={isBulkOrderFn}
                    isValueMeal={isValueMeal}
                    itemImageOnCartXpFlagExcludingBulkOrder={itemImageOnCartXpFlagExcludingBulkOrder}
                    isIrctcCartSession={isIrctcCartSession}
                    disableRedirectionToMenu={disableRedirectionToMenu}
                    isCroutonVisible={isCroutonVisible}
                    goToMenu={goToMenu}
                    isPopCart={isPopCart}
                />
            );
        },
        [
            cartResponse,
            menuItemsCalloutAnalytics,
            isBulkOrderFn,
            isValueMeal,
            itemImageOnCartXpFlagExcludingBulkOrder,
            isIrctcCartSession,
            disableRedirectionToMenu,
            isCroutonVisible,
            goToMenu,
            isPopCart,
        ],
    );

    const crossSellWidget = useCallback(
        (restaurantId?: number | string) => {
            if (isEmpty(cartResponse) || isCartBoltEnabled) {
                return null;
            }
            const restaurantIdString = !isEmpty(restaurantId) && isMROCart ? String(restaurantId) : undefined;
            return (
                <CrossSellM2ItemRenderer
                    onImpressionCrossSellM2Item={onImpressionCrossSellM2Item}
                    onImpressionAddItemCarousel={onImpressionAddItemCarousel}
                    onClickAddItemCarousel={onClickAddItemCarousel}
                    onClickItemDetailsCarousel={onClickItemDetailsCarousel}
                    onImpressionCarouselItemDetailsPopup={onImpressionCarouselItemDetailsPopup}
                    onImpressionCartItemDetails={onImpressionCartItemDetails}
                    onImpressionMagicItemsUnlockPopup={onImpressionMagicItemsUnlockPopup}
                    onClickDismissMagicItemsUnlockPopup={onClickDismissMagicItemsUnlockPopup}
                    onClickExploreMenuMagicItems={onClickExploreMenuMagicItems}
                    goToMenu={goToMenu}
                    restaurantId={restaurantIdString}
                />
            );
        },
        [
            cartResponse,
            isCartBoltEnabled,
            onImpressionCrossSellM2Item,
            onImpressionAddItemCarousel,
            onClickAddItemCarousel,
            onClickItemDetailsCarousel,
            onImpressionCarouselItemDetailsPopup,
            onImpressionCartItemDetails,
            onImpressionMagicItemsUnlockPopup,
            onClickDismissMagicItemsUnlockPopup,
            onClickExploreMenuMagicItems,
            goToMenu,
            isMROCart,
        ],
    );

    const snaccFreebieWidget = useCallback(() => {
        if (isEmpty(cartResponse) || !shouldShowFreebieWidget(cartResponse) || !isPopCart || isIrctcCartSession) {
            return null;
        }
        const widgetData = getFreebieWidgetData(cartResponse);
        return (
            <FreebieWidgetRenderer
                widgetData={widgetData as FreebieWidgetRenderingDetails}
                cartResponse={cartResponse}
            />
        );
    }, [cartResponse, isPopCart, isIrctcCartSession]);

    const stealDealWidget = useCallback(() => {
        if (isEmpty(cartResponse) || !shouldShowStealDealWidget(cartResponse) || !isPopCart) {
            return null;
        }

        // Extract steal deal widget from rendering details
        const stealDealWidgetData = getStealDealWidgetData(cartResponse);

        if (!stealDealWidgetData) {
            return null;
        }

        return (
            <StealDealWidgetRenderer
                widgetData={stealDealWidgetData as StealDealWidgetRenderingDetails}
                cartResponse={cartResponse}
            />
        );
    }, [cartResponse, isPopCart]);

    const renderWidget = useCallback(
        (widgetSection: RenderingDetails) => {
            const widgetType = widgetSection?.type?.toUpperCase();
            switch (widgetType) {
                case VALID_RENDERING_DETAILS_TYPE.MENU_ITEMS_ADDED_TO_CART:
                    return menuItemsWidget(isMROFlow(), widgetSection?.meta?.restaurantId);
                case VALID_RENDERING_DETAILS_TYPE.CART_CROSS_SELL_ITEMS_SUGGESTION:
                    return crossSellWidget(widgetSection?.meta?.restaurantId);
                case VALID_RENDERING_DETAILS_TYPE.SNACC_FREEBIE_WIDGET:
                    return snaccFreebieWidget();
                case VALID_RENDERING_DETAILS_TYPE.STEAL_DEAL_WIDGET:
                    return stealDealWidget();
                default:
                    return null;
            }
        },
        [crossSellWidget, menuItemsWidget, snaccFreebieWidget, stealDealWidget],
    );

    useEffectOnce(() => {
        const widgetSequence = JSON.stringify(
            widgetSections.reduce(
                (sequence, section, position) => {
                    sequence[section.type] = position + 1;
                    return sequence;
                },
                {} as Record<string, number>,
            ),
        );

        impressionCartWidgetRanking({
            impressionEvent: impressionEvent,
            widgetSequence,
            cartId: cartResponse?.data?.cartId || "",
        });
    });

    if (!isMRO) {
        return <>{widgetSections.map(renderWidget)}</>;
    }

    return <div ref={wrapperRef}>{widgetSections.map(renderWidget)}</div>;
}

export default memo(CartMenuItemsAndSuggestions);
